import { db } from './firebase';
import { collection, query, where, getDocs, doc, orderBy, getDoc, addDoc, serverTimestamp, setDoc, limit } from 'firebase/firestore';
import dayjs from 'dayjs';

// Revenue categories
const REVENUE_CATEGORIES = {
    MONTHLY_MEMBERSHIP: 'Membership Fees (Monthly)',
    YEARLY_MEMBERSHIP: 'Membership Fees (Yearly)', 
    RIGHT_WAY_WAGER: 'Right Way Wager Commission',
    TEN_TIL: '10til Commission'
};

// Pricing constants
const PRICING = {
    MONTHLY_MEMBERSHIP: 25,
    YEARLY_MEMBERSHIP: 250,
    RIGHT_WAY_WAGER_MIN: 5,
    RIGHT_WAY_WAGER_MAX: 35,
    TEN_TIL_MIN: 3,
    TEN_TIL_MAX: 23
};

/**
 * Firestore Collections Structure:
 * 
 * affiliateRegistrations:
 * - Document ID: auto-generated
 * - Fields: { userId, affiliateCode, registrationDate, email, displayName, createdAt }
 * 
 * affiliateRevenue:
 * - Document ID: auto-generated  
 * - Fields: { userId, affiliateCode, date, category, amount, description, createdAt }
 * 
 * users:
 * - Already exists with affiliateCode field for referrers
 */

export const affiliateService = {
    async generateAffiliateCode(firstName, lastName) {
        const firstInitial = firstName.charAt(0).toUpperCase();
        const lastInitial = lastName.charAt(0).toUpperCase();
        const baseCode = `${firstInitial}${lastInitial}`;

        try {
            const usersRef = collection(db, 'users');
            // Query for existing codes with this base - use limit to comply with Firestore rules
            const q = query(
                usersRef, 
                where('isAffiliate', '==', true),
                limit(10) // Comply with Firestore rules limit
            );

            const querySnapshot = await getDocs(q);
            let lastNumber = -1;

            if (!querySnapshot.empty) {
                querySnapshot.forEach(docSnapshot => {
                    const code = docSnapshot.data().affiliateCode;
                    if (code && code.startsWith(baseCode)) {
                        const numberPart = parseInt(code.substring(baseCode.length), 10);
                        if (numberPart > lastNumber) {
                            lastNumber = numberPart;
                        }
                    }
                });
            }

            // Implement odd-number sequencing: AR001 -> AR003 -> AR005 -> AR007
            const newNumber = lastNumber === -1 ? 1 : lastNumber + 2;
            const formattedNumber = newNumber.toString().padStart(3, '0');
            return `${baseCode}${formattedNumber}`;
        } catch (error) {
            console.error('Error generating affiliate code:', error);
            console.error('Error details:', error.code, error.message);
            // Fallback to a random code if query fails
            const randomNum = Math.floor(Math.random() * 1000);
            const formattedNumber = randomNum.toString().padStart(3, '0');
            console.log(`Using fallback code: ${baseCode}${formattedNumber}`);
            return `${baseCode}${formattedNumber}`;
        }
    },

    // === REGISTRATION FUNCTIONS ===

    // Add a new registration to Firestore
    async addRegistration(userId, affiliateCode, userEmail, userDisplayName) {
        try {
            const registrationData = {
                userId,
                affiliateCode,
                registrationDate: serverTimestamp(),
                email: userEmail,
                displayName: userDisplayName,
                createdAt: serverTimestamp()
            };

            const docRef = await addDoc(collection(db, 'affiliateRegistrations'), registrationData);
            return { success: true, id: docRef.id };
        } catch (error) {
            console.error('Error adding registration:', error);
            return { success: false, error: error.message };
        }
    },

    // Get registrations for a specific date
    async getRegistrationsForDate(date) {
        try {
            const dateKey = dayjs(date).format('YYYY-MM-DD');
            const startOfDay = dayjs(dateKey).startOf('day').toDate();
            const endOfDay = dayjs(dateKey).endOf('day').toDate();

            const registrationsRef = collection(db, 'affiliateRegistrations');
            const q = query(
                registrationsRef,
                where('registrationDate', '>=', startOfDay),
                where('registrationDate', '<=', endOfDay),
                orderBy('registrationDate', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const registrations = [];

            querySnapshot.forEach((doc) => {
                registrations.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            return registrations;
        } catch (error) {
            console.error('Error getting registrations for date:', error);
            return [];
        }
    },

    // Get total registrations count for a date
    async getRegistrationCountForDate(date) {
        const registrations = await this.getRegistrationsForDate(date);
        return registrations.length;
    },

    // Get registrations for a specific month
    async getRegistrationsForMonth(monthString) {
        try {
            const [year, month] = monthString.split('-');
            const startOfMonth = dayjs(`${year}-${month}-01`).startOf('month').toDate();
            const endOfMonth = dayjs(`${year}-${month}-01`).endOf('month').toDate();

            const registrationsRef = collection(db, 'affiliateRegistrations');
            const q = query(
                registrationsRef,
                where('registrationDate', '>=', startOfMonth),
                where('registrationDate', '<=', endOfMonth),
                orderBy('registrationDate', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const registrations = [];

            querySnapshot.forEach((doc) => {
                registrations.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            return registrations;
        } catch (error) {
            console.error('Error getting registrations for month:', error);
            return [];
        }
    },

    // Get summary stats for a month
    async getMonthSummary(monthString) {
        const registrations = await this.getRegistrationsForMonth(monthString);
        const totalRegistrations = registrations.length;
        const uniqueAffiliateCodes = [...new Set(registrations.map(r => r.affiliateCode))].length;

        return {
            totalRegistrations,
            uniqueAffiliateCodes
        };
    },

    // === REVENUE FUNCTIONS ===

    // Add revenue entry to Firestore
    async addRevenue(userId, affiliateCode, date, category, amount, description = '') {
        try {
            const revenueData = {
                userId,
                affiliateCode,
                date: dayjs(date).format('YYYY-MM-DD'),
                category,
                amount,
                description,
                createdAt: serverTimestamp()
            };

            const docRef = await addDoc(collection(db, 'affiliateRevenue'), revenueData);
            return { success: true, id: docRef.id };
        } catch (error) {
            console.error('Error adding revenue:', error);
            return { success: false, error: error.message };
        }
    },

    // Get revenue data for a specific date
    async getRevenueForDate(date) {
        try {
            const dateKey = dayjs(date).format('YYYY-MM-DD');
            
            const revenueRef = collection(db, 'affiliateRevenue');
            const q = query(
                revenueRef,
                where('date', '==', dateKey)
                // Note: Removed orderBy to avoid requiring composite index
                // Results are aggregated anyway, so order doesn't matter
            );

            const querySnapshot = await getDocs(q);
            const revenueEntries = [];

            querySnapshot.forEach((doc) => {
                revenueEntries.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            // Process entries into the expected format
            const dayRevenue = {
                date: dateKey,
                totalRevenue: 0,
                breakdown: {},
                summary: {
                    [REVENUE_CATEGORIES.MONTHLY_MEMBERSHIP]: 0,
                    [REVENUE_CATEGORIES.YEARLY_MEMBERSHIP]: 0,
                    [REVENUE_CATEGORIES.RIGHT_WAY_WAGER]: 0,
                    [REVENUE_CATEGORIES.TEN_TIL]: 0
                }
            };

            // Group revenue by affiliate code
            revenueEntries.forEach(entry => {
                if (!dayRevenue.breakdown[entry.affiliateCode]) {
                    dayRevenue.breakdown[entry.affiliateCode] = {
                        affiliateCode: entry.affiliateCode,
                        categories: {
                            [REVENUE_CATEGORIES.MONTHLY_MEMBERSHIP]: 0,
                            [REVENUE_CATEGORIES.YEARLY_MEMBERSHIP]: 0,
                            [REVENUE_CATEGORIES.RIGHT_WAY_WAGER]: 0,
                            [REVENUE_CATEGORIES.TEN_TIL]: 0
                        },
                        total: 0
                    };
                }

                dayRevenue.breakdown[entry.affiliateCode].categories[entry.category] += entry.amount;
                dayRevenue.breakdown[entry.affiliateCode].total += entry.amount;
                dayRevenue.summary[entry.category] += entry.amount;
                dayRevenue.totalRevenue += entry.amount;
            });

            return dayRevenue;
        } catch (error) {
            console.error('Error getting revenue for date:', error);
            return {
                date: dayjs(date).format('YYYY-MM-DD'),
                totalRevenue: 0,
                breakdown: {},
                summary: {
                    [REVENUE_CATEGORIES.MONTHLY_MEMBERSHIP]: 0,
                    [REVENUE_CATEGORIES.YEARLY_MEMBERSHIP]: 0,
                    [REVENUE_CATEGORIES.RIGHT_WAY_WAGER]: 0,
                    [REVENUE_CATEGORIES.TEN_TIL]: 0
                }
            };
        }
    },

    // Get revenue data for a month
    async getRevenueForMonth(monthString) {
        try {
            const [year, month] = monthString.split('-');
            const startOfMonth = dayjs(`${year}-${month}-01`).startOf('month');
            const endOfMonth = dayjs(`${year}-${month}-01`).endOf('month');
            
            const revenueRef = collection(db, 'affiliateRevenue');
            const q = query(
                revenueRef,
                where('date', '>=', startOfMonth.format('YYYY-MM-DD')),
                where('date', '<=', endOfMonth.format('YYYY-MM-DD')),
                orderBy('date', 'asc')
            );

            const querySnapshot = await getDocs(q);
            const revenueEntries = [];

            querySnapshot.forEach((doc) => {
                revenueEntries.push({
                    id: doc.id,
                    ...doc.data()
                });
            });

            const monthlyRevenue = {
                totalRevenue: 0,
                dailyBreakdown: [],
                summary: {
                    [REVENUE_CATEGORIES.MONTHLY_MEMBERSHIP]: 0,
                    [REVENUE_CATEGORIES.YEARLY_MEMBERSHIP]: 0,
                    [REVENUE_CATEGORIES.RIGHT_WAY_WAGER]: 0,
                    [REVENUE_CATEGORIES.TEN_TIL]: 0
                },
                affiliateBreakdown: {}
            };

            // Group by date first
            const dateGroups = {};
            revenueEntries.forEach(entry => {
                if (!dateGroups[entry.date]) {
                    dateGroups[entry.date] = [];
                }
                dateGroups[entry.date].push(entry);
            });

            // Process each date
            for (const [date, entries] of Object.entries(dateGroups)) {
                const dayRevenue = await this.getRevenueForDate(date);
                monthlyRevenue.dailyBreakdown.push(dayRevenue);
                monthlyRevenue.totalRevenue += dayRevenue.totalRevenue;

                // Add to summary
                Object.keys(dayRevenue.summary).forEach(category => {
                    monthlyRevenue.summary[category] += dayRevenue.summary[category];
                });

                // Add to affiliate breakdown
                Object.keys(dayRevenue.breakdown).forEach(affiliateCode => {
                    if (!monthlyRevenue.affiliateBreakdown[affiliateCode]) {
                        monthlyRevenue.affiliateBreakdown[affiliateCode] = {
                            affiliateCode,
                            categories: {
                                [REVENUE_CATEGORIES.MONTHLY_MEMBERSHIP]: 0,
                                [REVENUE_CATEGORIES.YEARLY_MEMBERSHIP]: 0,
                                [REVENUE_CATEGORIES.RIGHT_WAY_WAGER]: 0,
                                [REVENUE_CATEGORIES.TEN_TIL]: 0
                            },
                            total: 0
                        };
                    }

                    const dayMemberRevenue = dayRevenue.breakdown[affiliateCode];
                    Object.keys(dayMemberRevenue.categories).forEach(category => {
                        monthlyRevenue.affiliateBreakdown[affiliateCode].categories[category] += 
                            dayMemberRevenue.categories[category];
                    });
                    monthlyRevenue.affiliateBreakdown[affiliateCode].total += dayMemberRevenue.total;
                });
            }

            return monthlyRevenue;
        } catch (error) {
            console.error('Error getting revenue for month:', error);
            return {
                totalRevenue: 0,
                dailyBreakdown: [],
                summary: {
                    [REVENUE_CATEGORIES.MONTHLY_MEMBERSHIP]: 0,
                    [REVENUE_CATEGORIES.YEARLY_MEMBERSHIP]: 0,
                    [REVENUE_CATEGORIES.RIGHT_WAY_WAGER]: 0,
                    [REVENUE_CATEGORIES.TEN_TIL]: 0
                },
                affiliateBreakdown: {}
            };
        }
    },

    // Get revenue summary for current month
    async getCurrentMonthRevenueSummary() {
        const currentMonth = dayjs().format('YYYY-MM');
        return await this.getRevenueForMonth(currentMonth);
    },

    // Validate mathematical integrity for an entire month
    async validateMonthlyRevenueData(monthString) {
        const monthlyData = await this.getRevenueForMonth(monthString);
        let issues = [];

        // Check that daily totals sum to monthly total
        const dailySum = monthlyData.dailyBreakdown.reduce((sum, day) => sum + day.totalRevenue, 0);
        if (Math.abs(dailySum - monthlyData.totalRevenue) > 0.01) { // Allow for small floating point errors
            issues.push(`Monthly total (${monthlyData.totalRevenue}) doesn't match sum of daily totals (${dailySum})`);
        }

        // Check that summary totals match
        const summaryTotal = Object.values(monthlyData.summary).reduce((sum, amount) => sum + amount, 0);
        if (Math.abs(summaryTotal - monthlyData.totalRevenue) > 0.01) {
            issues.push(`Monthly total (${monthlyData.totalRevenue}) doesn't match summary total (${summaryTotal})`);
        }

        // Check affiliate breakdown totals
        const affiliateTotal = Object.values(monthlyData.affiliateBreakdown).reduce((sum, affiliate) => sum + affiliate.total, 0);
        if (Math.abs(affiliateTotal - monthlyData.totalRevenue) > 0.01) {
            issues.push(`Monthly total (${monthlyData.totalRevenue}) doesn't match affiliate breakdown total (${affiliateTotal})`);
        }

        return {
            isValid: issues.length === 0,
            issues
        };
    },

    // Validate all revenue data integrity
    async validateAllRevenueData() {
        let issues = [];

        // Validate current month
        const currentMonth = dayjs().format('YYYY-MM');
        const monthValidation = await this.validateMonthlyRevenueData(currentMonth);
        if (!monthValidation.isValid) {
            issues.push(`Month ${currentMonth}: ${monthValidation.issues.join(', ')}`);
        }

        return {
            isValid: issues.length === 0,
            issues,
            monthsChecked: 1
        };
    },

    // === REFERRAL SYSTEM METHODS ===

    // Validate if a referral code exists AND belongs to a user with
    // granted affiliate access. Invite-only lockdown (2026-10-30): the
    // program is admin-granted via the manage-affiliates function, which
    // stamps affiliateAccess { status: 'granted', code, ... } on the
    // user doc. Legacy auto-generated codes stop validating here because
    // their owners have no affiliateAccess field — no data migration.
    async validateReferralCode(referralCode) {
        if (!referralCode || referralCode.trim() === '') {
            return { isValid: false, error: 'Referral code is required' };
        }

        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('affiliateCode', '==', referralCode.trim().toUpperCase()), limit(1));
            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                return { isValid: false, error: 'Invalid referral code' };
            }

            // Get the first (and should be only) user with this code
            const referrerDoc = querySnapshot.docs[0];
            const referrerData = referrerDoc.data();

            // Only codes owned by a currently-granted affiliate are valid.
            // Same generic error as a nonexistent code — don't leak status.
            if (!referrerData.affiliateAccess || referrerData.affiliateAccess.status !== 'granted') {
                return { isValid: false, error: 'Invalid referral code' };
            }

            return {
                isValid: true,
                referrer: {
                    id: referrerDoc.id,
                    displayName: referrerData.displayName,
                    affiliateCode: referrerData.affiliateCode
                }
            };
        } catch (error) {
            console.error('Error validating referral code:', error);
            return { isValid: false, error: 'Unable to validate referral code. Please try again.' };
        }
    },

    // Create referral relationship between referrer and new user
    async createReferralRelationship(referrerId, newUserId, newUserData) {
        try {
            const batch = [];
            
            // Update referrer's document to add new user to their referralUsers array
            const referrerRef = doc(db, 'users', referrerId);
            const referrerUpdate = {
                referralUsers: newUserId // Will be converted to array union in the actual update
            };

            // Update new user's document with referrer information
            const newUserRef = doc(db, 'users', newUserId);
            const newUserUpdate = {
                referredByCode: newUserData.referredByCode,
                masterUser: referrerId
            };

            // We'll return the update objects so they can be applied in the signup process
            return {
                success: true,
                referrerUpdate: {
                    ref: referrerRef,
                    data: referrerUpdate
                },
                newUserUpdate: {
                    ref: newUserRef,
                    data: newUserUpdate
                }
            };
        } catch (error) {
            console.error('Error creating referral relationship:', error);
            return {
                success: false,
                error: 'Unable to create referral relationship'
            };
        }
    },

    // Maintain mapping of referrer -> array of referred users' affiliate codes
    async addReferredAffiliateCode(referrerId, newAffiliateCode, newUserId) {
        try {
            const mappingRef = doc(db, 'affiliateReferrals', referrerId);
            const snapshot = await getDoc(mappingRef);

            // Use map field `referredUsers` keyed by affiliateCode -> userUid
            const mapFieldPath = `referredUsers.${newAffiliateCode}`;

            if (snapshot.exists()) {
                // Merge the single map entry into referredUsers
                await setDoc(mappingRef, {
                    [mapFieldPath]: newUserId,
                    updatedAt: serverTimestamp()
                }, { merge: true });
            } else {
                const initialMap = {};
                initialMap[newAffiliateCode] = newUserId;

                await setDoc(mappingRef, {
                    referrerUserId: referrerId,
                    referredUsers: initialMap,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
            }
            return { success: true };
        } catch (error) {
            console.error('Error updating affiliateReferrals mapping:', error);
            return { success: false, error: error.message };
        }
    },

    // Get referred affiliate codes and users for dashboard display
    async getReferredAffiliateCodes(referrerId) {
        try {
            const mappingRef = doc(db, 'affiliateReferrals', referrerId);
            const snapshot = await getDoc(mappingRef);

            if (!snapshot.exists()) {
                return {
                    success: true,
                    referredAffiliateCodes: [],
                    referredUsers: [],
                    referredUsersMap: {},
                    count: 0
                };
            }

            const data = snapshot.data();

            // New shape: referredUsers is a map { [affiliateCode]: userUid }
            // Legacy shape: referredAffiliateCodes: [], referredUsers: [] (parallel arrays)
            let referredUsersMap = {};
            if (data.referredUsers && typeof data.referredUsers === 'object' && !Array.isArray(data.referredUsers)) {
                referredUsersMap = data.referredUsers;
            } else if (Array.isArray(data.referredAffiliateCodes) && Array.isArray(data.referredUsers)) {
                // Build map from parallel arrays for backward compatibility
                data.referredAffiliateCodes.forEach((code, idx) => {
                    const uid = data.referredUsers[idx] || null;
                    if (code) referredUsersMap[code] = uid;
                });
            }

            const referredAffiliateCodes = Object.keys(referredUsersMap);
            const referredUsers = Object.values(referredUsersMap);

            return {
                success: true,
                referredAffiliateCodes,
                referredUsers,
                referredUsersMap,
                count: referredAffiliateCodes.length,
                createdAt: data.createdAt,
                updatedAt: data.updatedAt
            };
        } catch (error) {
            console.error('Error getting referred affiliate codes:', error);
            return {
                success: false,
                error: error.message,
                referredAffiliateCodes: [],
                referredUsers: [],
                referredUsersMap: {},
                count: 0
            };
        }
    },



    // Get all users referred by a specific user
    async getReferredUsers(userId) {
        try {
            const usersRef = collection(db, 'users');
            const q = query(usersRef, where('masterUser', '==', userId));
            const querySnapshot = await getDocs(q);

            const referredUsers = [];
            querySnapshot.forEach((doc) => {
                const userData = doc.data();
                referredUsers.push({
                    id: doc.id,
                    displayName: userData.displayName,
                    email: userData.email,
                    profileCreatedAt: userData.profileCreatedAt,
                    referredByCode: userData.referredByCode
                });
            });

            return {
                success: true,
                users: referredUsers,
                count: referredUsers.length
            };
        } catch (error) {
            console.error('Error getting referred users:', error);
            return {
                success: false,
                error: 'Unable to get referred users',
                users: [],
                count: 0
            };
        }
    },

    // Get referral statistics for a user
    async getReferralStats(userId) {
        try {
            const referredUsersResult = await this.getReferredUsers(userId);
            
            if (!referredUsersResult.success) {
                return referredUsersResult;
            }

            const totalReferred = referredUsersResult.count;
            const referredThisMonth = referredUsersResult.users.filter(user => {
                if (!user.profileCreatedAt) return false;
                const userDate = dayjs(user.profileCreatedAt.toDate ? user.profileCreatedAt.toDate() : user.profileCreatedAt);
                return userDate.isSame(dayjs(), 'month');
            }).length;

            return {
                success: true,
                stats: {
                    totalReferred,
                    referredThisMonth,
                    users: referredUsersResult.users
                }
            };
        } catch (error) {
            console.error('Error getting referral stats:', error);
            return {
                success: false,
                error: 'Unable to get referral statistics'
            };
        }
    }
};